import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Stop from "@mui/icons-material/Stop";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import OnlinePrediction from "@mui/icons-material/OnlinePrediction";
import Autorenew from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useResponsive } from "../../core/hooks/useResponsive";
import { useSocket } from "../../core/hooks/useSocket";
import useCookie from "../../core/hooks/useCookie";
import { useDataFetch } from "../../core/hooks/useDataFetch";
import { useAppState } from "../../core/store/contextProvider";
import { Config } from "../../config/config";
import { PredictionStatus } from "../../config/constants";
import PredictionGrid from "./components/PredictionGrid";
import TradeLogGrid from "../Trading/components/TradeLogGrid";

const DEFAULT_INTERVAL_MS = 60000;

/**
 * Seconds until the next poll, with a ring showing how far through the interval we are.
 * Driven by the session's own scanInterval so it stays honest if that is ever retuned.
 */
const RefreshCountdown = ({ lastUpdated, intervalMs }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const total = Math.max(1000, Number(intervalMs) || DEFAULT_INTERVAL_MS);
  const since = lastUpdated ? now - new Date(lastUpdated).getTime() : 0;
  const remaining = Math.max(0, total - since);
  const seconds = Math.ceil(remaining / 1000);
  // A poll that has not landed yet reads as due rather than running the ring backwards.
  const progress = Math.min(100, Math.max(0, (since / total) * 100));

  return (
    <Tooltip title={seconds > 0 ? `Next refresh in ${seconds}s` : "Refreshing"}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            variant="determinate"
            value={progress}
            size={20}
            thickness={5}
            color={seconds > 0 ? "primary" : "success"}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Autorenew
              sx={{
                fontSize: 11,
                color: "text.secondary",
                animation: seconds > 0 ? "none" : "prediction-spin 1s linear infinite",
                "@keyframes prediction-spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 26 }}>
          {seconds > 0 ? `${seconds}s` : "now"}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const statusChip = (status) => {
  switch (status) {
    case PredictionStatus.Running:
      return { label: "Running", color: "success" };
    case PredictionStatus.Stopped:
      return { label: "Stopped", color: "info" };
    case PredictionStatus.ReadyToStart:
      return { label: "Ready", color: "default" };
    case PredictionStatus.OutsideWindow:
      return { label: "Market closed", color: "warning" };
    case PredictionStatus.Error:
      return { label: "Error", color: "error" };
    default:
      return { label: String(status || "—"), color: "default" };
  }
};

const LivePrediction = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { isPhone } = useResponsive();
  const { feedData, connect } = useSocket("predictionProcess");
  const { callAPI } = useDataFetch();
  const { getToken } = useCookie();
  const { theme: appTheme } = useAppState();
  const isLive = appTheme?.mode === "LT";

  const [check, setCheck] = useState(null);
  const [status, setStatus] = useState(null);
  const [notice, setNotice] = useState(null);
  const [budget, setBudget] = useState(25000);
  const [topN, setTopN] = useState(10);
  const [selectedToken, setSelectedToken] = useState(null);
  const [buying, setBuying] = useState(false);

  const runCheck = async () => {
    const res = await callAPI(Config.ITradeAPI.checkPrediction);
    const data = res?.data;
    setCheck(data ?? null);
    setStatus(data?.predictionStatus ?? PredictionStatus.ReadyToStart);
    if (data?.defaultBudget) setBudget(data.defaultBudget);
    if (data?.displayTop) setTopN(data.displayTop);
    if (data?.predictionStatus === PredictionStatus.OutsideWindow) {
      setNotice({ severity: "warning", message: data.message });
    }
  };

  const handleStart = async () => {
    const res = await callAPI(Config.ITradeAPI.startPrediction, { topN: Number(topN) });
    const data = res?.data;
    if (res?.error) {
      setNotice({ severity: "error", message: res.error || "Start prediction failed" });
      return;
    }
    if (data?.status) {
      setStatus(PredictionStatus.Running);
      setNotice({
        severity: "info",
        message: `Polling ${data.tokenCount} symbols every minute, showing the top ${data.displayTop}.`,
      });
    } else {
      setNotice({ severity: "error", message: data?.errorMessage || data?.errorMsg || "Start prediction failed" });
    }
  };

  const handleStop = async () => {
    const res = await callAPI(Config.ITradeAPI.stopPrediction);
    const data = res?.data;
    if (res?.error) {
      setNotice({ severity: "error", message: res.error || "Stop prediction failed" });
      return;
    }
    if (data?.status) {
      setStatus(PredictionStatus.Stopped);
    } else {
      setNotice({ severity: "error", message: data?.errorMessage || data?.errorMsg || "Stop prediction failed" });
    }
  };

  const handleBuy = async () => {
    if (!selectedToken) {
      setNotice({ severity: "warning", message: "Select a symbol row first." });
      return;
    }
    setBuying(true);
    try {
      const res = await callAPI(Config.ITradeAPI.buyPrediction, {
        token: selectedToken,
        budget: Number(budget),
      });
      const data = res?.data;
      if (res?.error) {
        setNotice({ severity: "error", message: res.error || "Buy failed" });
        return;
      }
      if (data?.status) {
        setNotice({
          severity: "success",
          message: `${data.virtual ? "Paper" : "Live"} order placed: ${data.qty} x ${data.symbol} @ ${data.price} = ${data.amount}`,
        });
      } else {
        setNotice({ severity: "error", message: data?.errorMessage || data?.errorMsg || "Buy failed" });
      }
    } finally {
      setBuying(false);
    }
  };

  useEffect(() => {
    const profileID = getToken()?.profileID;
    if (profileID) connect(profileID);
    runCheck();
  }, []);

  useEffect(() => {
    if (feedData?.predictionStatus) {
      setStatus(feedData.predictionStatus);
      if (feedData.predictionStatus === PredictionStatus.Error && feedData.message) {
        setNotice({ severity: "error", message: feedData.message });
      }
    }
  }, [feedData?.predictionStatus, feedData?.message]);

  if (check === null) {
    return <div />;
  }

  const chip = statusChip(status);
  const isRunning = status === PredictionStatus.Running;
  const predictions = feedData?.predictions || [];
  const selectedRow = predictions.find((r) => String(r.token) === String(selectedToken));
  const qtyPreview =
    selectedRow && selectedRow.close > 0 ? Math.floor(Number(budget) / selectedRow.close) : null;

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: alpha(primary, 0.02),
        pb: 1.5,
        pt: { xs: 0.75, md: 1.25 },
        px: { xs: 0.75, sm: 1.25, md: 1.5 },
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <OnlinePrediction sx={{ fontSize: isPhone ? 22 : 28, color: primary }} />
            <Typography variant={isPhone ? "h6" : "h5"} fontWeight={800} letterSpacing="-0.02em" noWrap>
              Live Prediction
            </Typography>
            <Chip size="small" label={chip.label} color={chip.color} variant={chip.color === "default" ? "outlined" : "filled"} />
            {feedData?.lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                updated {new Date(feedData.lastUpdated).toLocaleTimeString()}
              </Typography>
            )}
            {isRunning && feedData?.lastUpdated && (
              <RefreshCountdown
                lastUpdated={feedData.lastUpdated}
                intervalMs={feedData.scanInterval}
              />
            )}
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(primary, 0.14)}`,
            boxShadow: `0 8px 24px ${alpha("#000", 0.04)}`,
            p: { xs: 1.25, md: 1.5 },
            mb: 1.5,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrow />}
              onClick={handleStart}
              disabled={isRunning}
              sx={{ fontWeight: 700 }}
            >
              Start prediction
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Stop />}
              onClick={handleStop}
              disabled={!isRunning}
              sx={{ fontWeight: 700 }}
            >
              Stop
            </Button>

            <TextField
              select
              size="small"
              label="Display top"
              value={topN}
              onChange={(e) => setTopN(e.target.value)}
              disabled={isRunning}
              sx={{ width: { xs: "100%", sm: 130 } }}
            >
              {(check?.displayTopOptions || [10, 20, 50, 100]).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />

            <TextField
              size="small"
              label="Budget"
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              sx={{ width: { xs: "100%", sm: 150 } }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
            <Button
              variant="contained"
              color={isLive ? "error" : "success"}
              startIcon={<ShoppingCart />}
              onClick={handleBuy}
              disabled={!selectedToken || buying || !(Number(budget) > 0)}
              sx={{ fontWeight: 700 }}
            >
              {isLive ? "Buy (live)" : "Buy (paper)"}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {selectedRow
              ? `${selectedRow.symbol} at ${Number(selectedRow.close).toFixed(2)} — buys ${qtyPreview} share(s) for about ${(qtyPreview * selectedRow.close).toFixed(2)}`
              : "Click a row in the grid to select a symbol, then Buy."}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            p: { xs: 1, md: 1.5 },
            border: `1px solid ${alpha(primary, 0.12)}`,
            mb: 1.5,
          }}
        >
          <PredictionGrid
            predictions={predictions}
            selectedToken={selectedToken}
            onSelect={setSelectedToken}
            targetGainPct={check?.targetGainPct}
            horizonDays={check?.horizonDays}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            p: { xs: 1, md: 1.5 },
            border: `1px solid ${alpha(primary, 0.12)}`,
          }}
        >
          <TradeLogGrid tradeLog={feedData?.tradeLog} showBought={false} />
        </Paper>
      </Box>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={notice?.severity === "error" ? 10000 : 6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={notice?.severity || "info"}
          variant="filled"
          action={
            <IconButton size="small" color="inherit" aria-label="Dismiss" onClick={() => setNotice(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ borderRadius: 2, maxWidth: 560, alignItems: "center" }}
        >
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LivePrediction;
